import { type AnyAction } from 'redux';
import { call, CallEffect, ForkEffect, put, PutEffect, takeLatest, takeEvery } from 'redux-saga/effects';

import { BuilderCostingActionType } from './enums';
import {
	editFCCosting,
	editFCLumenCosting,
	fetchFCCosting,
	fetchFCMaterialCosting,
	addFCMaterialCosting,
	editFCMaterialCosting,
	fetchFCLumenCosting,
	fetchCabinetMaterials,
	editCabinetMaterials,
	fetchCounterTopMaterials,
	editCounterTopMaterials,
	fetchWallTiles,
	addWallTile,
	updateWallTile,
	fetchElectricalModules,
	editElectricalModules,
	fetchPaintMaterials,
	editPaintMaterials,
	fetchMouldingTypes,
	fetchMouldingPatterns,
	editMouldingTypes,
	editMouldingPatterns,
	fetchSFBMaterials,
	editSFBMaterials,
	fetchCurtainMaterials,
	editCurtainMaterials,
	fetchBuilderChargeConfigs,
	upsertBuilderChargeConfig
} from './api';

type BuilderCostingSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type BuilderCostingSagaFormEffect = Generator<ForkEffect<void>>;

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

const normalizePaintMaterials = (data: Record<string, any>) =>
	(data?.paintMaterials ?? data?.paint_materials ?? []).map((material: Record<string, any>) => ({
		...material,
		paintingMethod: material?.paintingMethod ?? material?.painting_method ?? null,
		surfaceTypes: material?.surfaceTypes ?? material?.surface_types ?? [],
		paintCost: material?.paintCost ?? material?.paint_cost ?? 0,
		labourCost: material?.labourCost ?? material?.labour_cost ?? 0,
		paintSpread: material?.paintSpread ?? material?.paint_spread ?? 0,
		paintComponents: material?.paintComponents ?? material?.paint_components ?? []
	}));

export function* fetchFCCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchFCCosting);
		yield put({
			type: BuilderCostingActionType.STORE_FC_COSTING,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editFCCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;
	try {
		yield call(editFCCosting, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchFCMaterialCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchFCMaterialCosting);
		yield put({
			type: BuilderCostingActionType.STORE_FC_MATERIAL_COSTING,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editFCMaterialCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;
	try {
		yield call(editFCMaterialCosting, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* addFCMaterialCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;
	try {
		yield call(addFCMaterialCosting, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchFCLumenCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchFCLumenCosting);
		yield put({
			type: BuilderCostingActionType.STORE_FC_LUMEN_COSTING,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editFCLumenCostingEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;
	try {
		yield call(editFCLumenCosting, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchCabinetMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB, successCB, type } = action.payload;

	try {
		const { data }: any = yield call(fetchCabinetMaterials, type);

		yield put({
			type: BuilderCostingActionType.STORE_CABINET_MATERIALS,
			payload: {
				type,
				data
			}
		});
		successCB?.(data);
	} catch (error: unknown) {
		yield put({ type: BuilderCostingActionType.ERROR_CABINET_MATERIALS, payload: { type, error: getErrorMessage(error) } });
		errorCB?.(getErrorMessage(error));
	}
}

export function* fetchWallPanelMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB, type } = action.payload;

	try {
		const { data }: any = yield call(fetchCabinetMaterials, type);

		yield put({
			type: BuilderCostingActionType.STORE_WALL_PANEL_MATERIALS,
			payload: {
				type,
				data
			}
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchLightingMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB, type } = action.payload;
	try {
		const { data }: any = yield call(fetchCabinetMaterials, type);
		yield put({
			type: BuilderCostingActionType.STORE_LIGHTS_MATERIALS,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editWallPanelMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		yield call(editCabinetMaterials, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* editCabinetMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		yield call(editCabinetMaterials, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchCounterTopMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchCounterTopMaterials);
		yield put({
			type: BuilderCostingActionType.STORE_COUNTER_TOP_MATERIALS,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editCounterTopMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		yield call(editCounterTopMaterials, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchWallTilesEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchWallTiles);
		yield put({
			type: BuilderCostingActionType.STORE_WALL_TILES,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* addWallTileEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values } = action.payload;

	try {
		yield call(addWallTile, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* updateWallTileEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		yield call(updateWallTile, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB?.();
	}
}

export function* fetchElectricalModulesEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchElectricalModules);
		yield put({
			type: BuilderCostingActionType.STORE_ELECTRICAL_MODULES,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editElectricalModulesEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values } = action.payload;

	try {
		yield call(editElectricalModules, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchElectricalMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB, type } = action.payload;

	try {
		const { data }: any = yield call(fetchCabinetMaterials, type);

		yield put({
			type: BuilderCostingActionType.STORE_ELECTRICAL_MATERIALS,
			payload: {
				type,
				data
			}
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editElectricalMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		yield call(editCabinetMaterials, values);
		successCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	} finally {
		finalCB();
	}
}

export function* fetchPaintMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchPaintMaterials);
		const paintMaterials = normalizePaintMaterials(data);
		yield put({
			type: BuilderCostingActionType.STORE_PAINT_MATERIALS,
			payload: paintMaterials
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchMouldingTypesEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchMouldingTypes);
		yield put({
			type: BuilderCostingActionType.STORE_MOULDING_TYPES,
			payload: data.mouldingTypes
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editPaintMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { values, successCB, errorCB, finalCB } = action.payload;

	try {
		const { data }: any = yield call(editPaintMaterials, { paintMaterials: values });
		yield put({
			type: BuilderCostingActionType.STORE_PAINT_MATERIALS,
			payload: normalizePaintMaterials(data)
		});
		successCB();
		finalCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchMouldingPatternsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchMouldingPatterns);
		yield put({
			type: BuilderCostingActionType.STORE_MOULDING_PATTERNS,
			payload: data.mouldingPatterns
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editMouldingTypesEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		const { data }: any = yield call(editMouldingTypes, { mouldingTypes: values });
		yield put({
			type: BuilderCostingActionType.STORE_MOULDING_TYPES,
			payload: data.mouldingTypes
		});
		successCB();
		finalCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editMouldingPatternsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		const { data }: any = yield call(editMouldingPatterns, { mouldingPatterns: values });
		yield put({
			type: BuilderCostingActionType.STORE_MOULDING_PATTERNS,
			payload: data.mouldingPatterns
		});
		successCB();
		finalCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchSFBMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchSFBMaterials);
		yield put({
			type: BuilderCostingActionType.STORE_SFB_MATERIALS,
			payload: data.sfb
		});
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* editSFBMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		const { data }: any = yield call(editSFBMaterials, { sfb: values });
		yield put({
			type: BuilderCostingActionType.STORE_SFB_MATERIALS,
			payload: data.sfb
		});
		successCB();
		finalCB();
	} catch (error: unknown) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchCurtainMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchCurtainMaterials);
		yield put({
			type: BuilderCostingActionType.STORE_CURTAIN_MATERIALS,
			payload: data
		});
	} catch (error: any) {
		errorCB(getErrorMessage(error));
	}
}

export function* editCurtainMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { successCB, errorCB, values, finalCB } = action.payload;

	try {
		const { data }: any = yield call(editCurtainMaterials, values);
		yield put({
			type: BuilderCostingActionType.STORE_CURTAIN_MATERIALS,
			payload: data
		});
		successCB();
		finalCB();
	} catch (error: any) {
		errorCB(getErrorMessage(error));
	}
}

export function* fetchBuilderChargeConfigsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { builderUsage, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchBuilderChargeConfigs, builderUsage);
		yield put({
			type: BuilderCostingActionType.STORE_BUILDER_CHARGE_CONFIGS,
			payload: data
		});
	} catch (error: any) {
		if (errorCB) errorCB(getErrorMessage(error));
	}
}

export function* upsertBuilderChargeConfigEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { values, successCB, errorCB, finalCB } = action.payload;

	try {
		yield call(upsertBuilderChargeConfig, values);
		yield put({
			type: BuilderCostingActionType.FETCH_BUILDER_CHARGE_CONFIGS,
			payload: {
				errorCB
			}
		});
		if (successCB) successCB();
	} catch (error: any) {
		if (errorCB) errorCB(getErrorMessage(error));
	} finally {
		if (finalCB) finalCB();
	}
}

export function* fetchWallTilesSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_WALL_TILES, fetchWallTilesEffect);
}

export function* addWallTileSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.ADD_WALL_TILE, addWallTileEffect);
}

export function* updateWallTileSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.UPDATE_WALL_TILE, updateWallTileEffect);
}

export function* fetchFCCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_FC_COSTING, fetchFCCostingEffect);
}

export function* editFCCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_FC_BUILDER_COSTING, editFCCostingEffect);
}

export function* fetchFCMaterialCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_FC_MATERIAL_COSTING, fetchFCMaterialCostingEffect);
}

export function* editFCMaterialCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_FC_MATERIAL_COSTING, editFCMaterialCostingEffect);
}

export function* addFCMaterialCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.ADD_FC_MATERIAL_COSTING, addFCMaterialCostingEffect);
}

export function* fetchFCLumenCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_FC_LUMEN_COSTING, fetchFCLumenCostingEffect);
}
export function* editFCLumenCostingSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_FC_LUMEN_COSTING, editFCLumenCostingEffect);
}

export function* fetchCabinetMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeEvery(BuilderCostingActionType.FETCH_CABINET_MATERIALS, fetchCabinetMaterialsEffect);
}

export function* fetchWallPanelMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeEvery(BuilderCostingActionType.FETCH_WALL_PANEL_MATERIALS, fetchWallPanelMaterialsEffect);
}

export function* editCabinetMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_CABINET_MATERIALS, editCabinetMaterialsEffect);
}

export function* editWallPanelMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_WALL_PANEL_MATERIALS, editWallPanelMaterialsEffect);
}

export function* fetchCounterTopMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_COUNTER_TOP_MATERIALS, fetchCounterTopMaterialsEffect);
}

export function* editCounterTopMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_COUNTER_TOP_MATERIALS, editCounterTopMaterialsEffect);
}

export function* fetchLightingMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeEvery(BuilderCostingActionType.FETCH_LIGHTS_MATERIALS, fetchLightingMaterialsEffect);
}

export function* fetchDriverMaterialsEffect(action: AnyAction): BuilderCostingSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchCabinetMaterials, 'DRIVERS');
		yield put({
			type: BuilderCostingActionType.STORE_DRIVER_MATERIALS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({ type: BuilderCostingActionType.STORE_DRIVER_MATERIALS, payload: [] });
		errorCB(getErrorMessage(error));
	}
}

export function* fetchDriverMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeEvery(BuilderCostingActionType.FETCH_DRIVER_MATERIALS, fetchDriverMaterialsEffect);
}

export function* fetchElectricalModulesSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_ELECTRICAL_MODULES, fetchElectricalModulesEffect);
}

export function* editElectricalModulesSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_ELECTRICAL_MODULES, editElectricalModulesEffect);
}

export function* fetchElectricalMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeEvery(BuilderCostingActionType.FETCH_ELECTRICAL_MATERIALS, fetchElectricalMaterialsEffect);
}

export function* editElectricalMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_ELECTRICAL_MATERIALS, editElectricalMaterialsEffect);
}

export function* fetchPaintMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_PAINT_MATERIALS, fetchPaintMaterialsEffect);
}

export function* editPaintMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_PAINT_MATERIALS, editPaintMaterialsEffect);
}

export function* fetchMouldingTypesSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_MOULDING_TYPES, fetchMouldingTypesEffect);
}

export function* fetchMouldingPatternsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_MOULDING_PATTERNS, fetchMouldingPatternsEffect);
}

export function* editMouldingTypesSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_MOULDING_TYPES, editMouldingTypesEffect);
}

export function* editMouldingPatternsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_MOULDING_PATTERNS, editMouldingPatternsEffect);
}

export function* fetchSFBMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_SFB_MATERIALS, fetchSFBMaterialsEffect);
}

export function* editSFBMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_SFB_MATERIALS, editSFBMaterialsEffect);
}

export function* fetchCurtainMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_CURTAIN_MATERIALS, fetchCurtainMaterialsEffect);
}

export function* editCurtainMaterialsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.EDIT_CURTAIN_MATERIALS, editCurtainMaterialsEffect);
}

export function* fetchBuilderChargeConfigsSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.FETCH_BUILDER_CHARGE_CONFIGS, fetchBuilderChargeConfigsEffect);
}

export function* upsertBuilderChargeConfigSaga(): BuilderCostingSagaFormEffect {
	yield takeLatest(BuilderCostingActionType.UPSERT_BUILDER_CHARGE_CONFIG, upsertBuilderChargeConfigEffect);
}
